import { PayFrequencyValue } from '@nxsys/components/AppPayFrequencySelect/type';
import type { TaxPeriodValue } from '@nxsys/components/AppTaxPeriodDropdown';
import { AppDispatch } from '@nxsys/store';
import {
  getAnalyticsAgencyInvoice,
  getAnalyticsCompaniesNoPayroll,
  getAnalyticsPayments,
  getAnalyticsPayrollCost,
  getAnalyticsPayslipsProcessed,
  getAnalyticsPensionDocument,
  getAnalyticsRegistration,
  getAnalyticsInvoicingValues,
  getAnalyticsPayrollValues,
} from '@nxsys/store/slices/analytics/actions';
import type { Moment } from 'moment';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { roles } from '@nxsys/store/constants';
import {
  buildAnalyticsFilterParams,
  defaultDateRange,
} from './analyticsFilterUtils';

export type AnalyticsFilterContextValue = {
  dateRange: [Moment | null, Moment | null] | null;
  taxPeriod: TaxPeriodValue | undefined;
  payFrequencies: PayFrequencyValue | undefined;
  employerIds: string[];
  setDateRange: (dates: [Moment | null, Moment | null] | null) => void;
  setTaxPeriod: (value: TaxPeriodValue) => void;
  setPayFrequencies: (value: PayFrequencyValue | undefined) => void;
  setEmployerIds: (value: string[]) => void;
};

const AnalyticsFilterContext =
  createContext<AnalyticsFilterContextValue | null>(null);

export const AnalyticsFilterProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [dateRange, setDateRange] = useState<
    [Moment | null, Moment | null] | null
  >(defaultDateRange);
  const [taxPeriod, setTaxPeriod] = useState<TaxPeriodValue | undefined>(
    undefined,
  );
  const [payFrequencies, setPayFrequencies] = useState<
    PayFrequencyValue | undefined
  >(undefined);
  const [employerIds, setEmployerIds] = useState<string[]>([]);

  const { user } = useSelector((state: AppState) => state.appStore);

  // Auto-prefill employer dropdown for employer users with a single assigned company
  useEffect(() => {
    if (
      user?.role === roles.employer &&
      user?.companyUserIds?.length === 1
    ) {
      setEmployerIds(user.companyUserIds);
    }
  }, [user]);

  const fetchFilteredData = useCallback(() => {
    const params = buildAnalyticsFilterParams(
      dateRange,
      taxPeriod,
      payFrequencies,
      employerIds,
    );

    // Payslips processed overview filters should be limited to:
    // taxYear, employerIds, payFrequencies (no dateFrom/dateTo).
    const payslipsOverviewParams = {
      taxYear:
        taxPeriod?.taxYear !== undefined ? String(taxPeriod.taxYear) : undefined,
      taxPeriodFrom:
        taxPeriod?.taxPeriodFrom !== undefined
          ? String(taxPeriod.taxPeriodFrom)
          : undefined,
      taxPeriodTo:
        taxPeriod?.taxPeriodTo !== undefined
          ? String(taxPeriod.taxPeriodTo)
          : undefined,
      payFrequencies: payFrequencies ? payFrequencies.toString() : undefined,
      employerIds: employerIds.length ? employerIds.join(',') : undefined,
    };

    dispatch(getAnalyticsPensionDocument(params));
    dispatch(getAnalyticsPayments(params));
    dispatch(getAnalyticsPayrollCost(params));
    dispatch(getAnalyticsRegistration(params));
    dispatch(getAnalyticsPayslipsProcessed(payslipsOverviewParams));
    dispatch(getAnalyticsPayrollValues(params));
    dispatch(getAnalyticsInvoicingValues(params));
    dispatch(getAnalyticsAgencyInvoice(params));
    dispatch(getAnalyticsCompaniesNoPayroll(params));
  }, [dateRange, taxPeriod, payFrequencies, employerIds, dispatch]);

  useEffect(() => {
    fetchFilteredData();
  }, [fetchFilteredData]);

  const value: AnalyticsFilterContextValue = {
    dateRange,
    taxPeriod,
    payFrequencies,
    employerIds,
    setDateRange,
    setTaxPeriod,
    setPayFrequencies,
    setEmployerIds,
  };

  return (
    <AnalyticsFilterContext.Provider value={value}>
      {children}
    </AnalyticsFilterContext.Provider>
  );
};

export const useAnalyticsFilter = (): AnalyticsFilterContextValue => {
  const context = useContext(AnalyticsFilterContext);
  if (!context) {
    throw new Error(
      'useAnalyticsFilter must be used within AnalyticsFilterProvider',
    );
  }
  return context;
};
